/* Preload all 26 BSL fingerspelled-alphabet PNGs as in-memory blob
   URLs.
   ---------------------------------------------------------------
   The games (`Fingerspell Five`, `Fingerspell Quiz / Sprint`,
   `Fingerspell Player`, `Signspell`) reveal letters dynamically as
   the player types or as a word is animated. Without preloading,
   the first render of each letter pays a one-off network/disk hit
   and the letter "pops in" a beat later - jarring during a
   60-second sprint or a Wordle reveal.

   Why we don't only rely on the HTTP cache:
     Upstream proxies (e.g. Cloudflare, this dev preview environment)
     can send `Cache-Control: no-store` for image responses, which
     forces the browser to discard the disk cache entry the moment
     the response is received. The next time `<img src="...">`
     renders the same URL, a fresh network round-trip happens -
     exactly what we wanted to avoid.

     The robust fix used here:

       1. `fetch()` each alphabet PNG once, eagerly.
       2. Convert the response to a Blob.
       3. `URL.createObjectURL(blob)` to produce a `blob:` URL.
       4. Store the mapping `originalUrl -> blobUrl` in a module-level
          Map. The Blob holds the bytes alive in memory; the blob:
          URL is a local handle that the browser serves with zero
          network round-trips regardless of HTTP cache headers.

     Call sites use `getAlphabetSrc()` (from useDisplayPreferences)
     or, for the legacy raw-path caller, the new `cachedAlphabetUrl()`
     helper below. Both consult the cache transparently, so when a
     letter has been preloaded the returned URL is the local `blob:`
     URL; otherwise the original `/images/alphabet/...` URL is used
     (and the browser handles the fetch as normal).

   Idempotency:
     - We track in-flight folder warms in a Set so React 19's
       StrictMode double-invoke doesn't fire 26 duplicate fetches.
     - Warmed entries are kept for the lifetime of the page; blob
       URLs are NEVER revoked, since the games are long-lived and
       the alphabet is finite (78 images max across 3 skin tones). */

import { useEffect } from "react";

const ALPHABET = "abcdefghijklmnopqrstuvwxyz".split("");

/* Map of original URL (e.g. "/images/alphabet/a.png") to its blob
   URL (e.g. "blob:https://.../uuid"). Lookup is O(1). */
const urlMap = new Map();
/* In-flight + completed folder warms, to dedupe calls. */
const warmed = new Set();

const folderFor = (skinTone) => {
  const t = (skinTone || "original").toLowerCase();
  if (t === "medium") return "/images/alphabet/medium";
  if (t === "deep")   return "/images/alphabet/deep";
  return "/images/alphabet";
};

/* Public lookup. Returns the blob URL if a letter has been
   preloaded, otherwise returns the original URL unchanged. Safe
   to call from any code path - if preload hasn't completed yet
   the original URL still works (and once it does, future paints
   will hit the blob). */
export const cachedAlphabetUrl = (originalUrl) => {
  if (!originalUrl) return originalUrl;
  return urlMap.get(originalUrl) || originalUrl;
};

const warmFolder = async (folder) => {
  if (warmed.has(folder)) return;
  warmed.add(folder);
  await Promise.all(
    ALPHABET.map(async (letter) => {
      const original = `${folder}/${letter}.png`;
      if (urlMap.has(original)) return;
      try {
        const res = await fetch(original, { credentials: "same-origin" });
        if (!res.ok) return;
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        urlMap.set(original, blobUrl);
      } catch (e) {
        /* Offline / 404 - leave the entry empty so call sites fall
           back to the original URL on the next paint. */
      }
    })
  );
};

export const useAlphabetPreload = (skinTone) => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    warmFolder(folderFor(skinTone));
  }, [skinTone]);
};

export default useAlphabetPreload;
